import { useState } from "react";
import { NavLink, Outlet, useLocation, useParams, Navigate } from "react-router-dom";
import { Inbox as InboxIcon, MessageCircle, RefreshCw, Settings, BarChart2, Zap } from "lucide-react";
import { toast } from "sonner";
import { cn } from "@/lib/utils";
import { useBrandContext } from "@/contexts/brand-context";
import { useInboxUnread } from "@/hooks/use-inbox-unread";
import { useSyncInboxNow } from "@/hooks/use-sync-inbox-now";
import { UnreadBadge } from "@/components/inbox/unread-badge";
import { InboxRoutingSettings } from "@/components/inbox/routing-settings";
import { usePermissions } from "@/hooks/use-permissions";
import { useTranslation } from "react-i18next";

/**
 * Inbox shell — left rail with DMs / Comments tabs and an outlet for the
 * active sub-page. Mentions tab is intentionally absent in v1 (spec §12.2).
 */
export function InboxPage() {
  const { t } = useTranslation("inbox");
  const { brandSlug } = useParams<{ brandSlug: string }>();
  const { brand } = useBrandContext();
  const location = useLocation();
  const { data: unread } = useInboxUnread(brand.id);
  const permissions = usePermissions();
  const [routingOpen, setRoutingOpen] = useState(false);
  const syncNow = useSyncInboxNow(brand.id);

  // Bare /inbox redirects to /inbox/dms.
  if (location.pathname === `/${brandSlug}/inbox` || location.pathname === `/${brandSlug}/inbox/`) {
    return <Navigate to={`/${brandSlug}/inbox/dms`} replace />;
  }

  const tabs = [
    {
      to: `/${brandSlug}/inbox/dms`,
      label: t("tabs.dms"),
      icon: MessageCircle,
      unread: unread?.dms ?? 0,
    },
    {
      to: `/${brandSlug}/inbox/comments`,
      label: t("tabs.comments"),
      icon: InboxIcon,
      unread: unread?.comments ?? 0,
    },
    {
      to: `/${brandSlug}/inbox/automations`,
      label: t("tabs.automations"),
      icon: Zap,
      unread: 0,
    },
    {
      to: `/${brandSlug}/inbox/reports`,
      label: t("tabs.reports"),
      icon: BarChart2,
      unread: 0,
    },
  ];

  return (
    <div className="flex h-[calc(100vh-0px)] w-full">
      {/* Left tabs rail */}
      <aside className="w-48 flex-shrink-0 border-r border-border bg-surface-0">
        <div className="px-4 py-4 border-b border-border flex items-center justify-between">
          <h2 className="text-[13px] font-semibold">{t("title")}</h2>
          {permissions.canManageBrand && (
            <button
              type="button"
              aria-label={t("settings.ariaLabel")}
              className="text-muted-foreground hover:text-foreground"
              onClick={() => setRoutingOpen((v) => !v)}
            >
              <Settings size={14} />
            </button>
          )}
        </div>
        {routingOpen && (
          <div className="border-b border-border p-3">
            <InboxRoutingSettings
              brandId={brand.id}
              canManage={permissions.canManageBrand}
            />
          </div>
        )}
        <nav className="flex flex-col">
          {tabs.map((tab) => (
            <NavLink
              key={tab.to}
              to={tab.to}
              className={({ isActive }) =>
                cn(
                  "flex items-center justify-between gap-2 px-4 py-2.5 text-[13px] border-l-2 transition-colors",
                  isActive
                    ? "bg-accent text-accent-foreground border-primary font-medium"
                    : "text-muted-foreground border-transparent hover:bg-surface-2 hover:text-foreground",
                )
              }
            >
              <span className="flex items-center gap-2">
                <tab.icon size={14} />
                {tab.label}
              </span>
              <UnreadBadge count={tab.unread} />
            </NavLink>
          ))}
        </nav>
        {permissions.canTriggerSync && (
          <div className="border-t border-border p-3">
            <button
              type="button"
              disabled={syncNow.isPending}
              onClick={() =>
                syncNow.mutate(undefined, {
                  onSuccess: () => toast.success(t("sync.started")),
                  onError: () => toast.error(t("sync.error")),
                })
              }
              className="flex w-full items-center justify-center gap-2 rounded-md border border-border px-2 py-1.5 text-[12px] text-muted-foreground hover:bg-surface-2 hover:text-foreground disabled:opacity-60"
            >
              <RefreshCw
                size={13}
                className={cn(syncNow.isPending && "animate-spin")}
              />
              {syncNow.isPending ? t("sync.syncing") : t("sync.button")}
            </button>
          </div>
        )}
      </aside>

      {/* Content */}
      <div className="flex-1 min-w-0">
        <Outlet />
      </div>
    </div>
  );
}
